import type { components } from "@/api/types";

export type WalletOverview = components["schemas"]["WalletOverview"];
export type DepositRequestOut = components["schemas"]["DepositRequestOut"];
export type DepositPayment = components["schemas"]["DepositPaymentOut"];
export type DepositQuote = components["schemas"]["DepositQuote"];
export type WithdrawalRequestOut = components["schemas"]["WithdrawalRequestOut"];
export type TransactionItem = components["schemas"]["TransactionItem"];
export type TransactionType = components["schemas"]["TransactionType"];
export type RequestStatus = components["schemas"]["RequestStatus"];

/**
 * Статусы заявки. В контракте это свободная строка, здесь union — по нему
 * строятся ключи `wallet.requestStatus.*` и выбирается ветка экрана.
 * `paid` встречается только у вывода, у пополнения — никогда.
 */
const STATUSES = new Set(["pending", "approved", "rejected", "paid"]);

export function asRequestStatus(value: string): RequestStatus {
  return STATUSES.has(value) ? (value as RequestStatus) : "pending";
}

/**
 * Статусы платежа на стороне шлюза. В контракте это свободная строка — словарь
 * принадлежит NOWPayments, и фиксировать его enum'ом в нашей схеме значило бы
 * ломать ответ в день, когда провайдер добавит своё значение.
 *
 * Здесь union, потому что по нему выбирается ветка экрана и ключ
 * `wallet.payment.status.*`. Незнакомое значение сводится к `waiting`: «ждём»
 * — единственная безопасная трактовка неизвестного, она никому ничего не обещает.
 */
export type PaymentStatus =
  | "waiting"
  | "confirming"
  | "confirmed"
  | "sending"
  | "finished"
  | "partially_paid"
  | "failed"
  | "refunded"
  | "expired";

const PAYMENT_STATUSES = new Set<string>([
  "waiting",
  "confirming",
  "confirmed",
  "sending",
  "finished",
  "partially_paid",
  "failed",
  "refunded",
  "expired",
]);

export function asPaymentStatus(value: string): PaymentStatus {
  return PAYMENT_STATUSES.has(value) ? (value as PaymentStatus) : "waiting";
}

/** Статусы, после которых состояние платежа больше не изменится. */
const SETTLED = new Set<PaymentStatus>(["finished", "failed", "refunded", "expired"]);

/**
 * Нужно ли продолжать опрашивать заявку.
 *
 * Считается по платежу, а не по статусу заявки: `partially_paid` оставляет
 * заявку в `pending` навсегда — до вмешательства оператора, — и опрашивать её
 * каждые десять секунд бессмысленно.
 */
export function isPaymentPending(payment: DepositPayment | null | undefined): boolean {
  if (!payment) return false;
  const status = asPaymentStatus(payment.payment_status);
  return !SETTLED.has(status) && status !== "partially_paid";
}
